import { initialize } from "launchdarkly-js-client-sdk";
import type { AppEnvironmentValue } from "./env.ts";
import { AllFeatureFlags, type FeatureFlagKey } from "@packages/core/feature-flags/featureFlags.ts";
import { IS_DEMO } from "@packages/core-client/demo/isDemo.ts";

const LOCAL_STORAGE_FLAGS_KEY = "msw-devtools-feature-flags";

const DEMO_ENABLED_FLAGS: FeatureFlagKey[] = ["weekly-brief", "org-chart-viewer"];

async function initializeLocalStorageClient() {
    const { createLocalStorageLaunchDarklyClient } = await import("@squide/firefly");

    const flags: Record<string, unknown> = Object.fromEntries(Object.entries(AllFeatureFlags).map(([x, y]) => [x, y.defaultValue]));

    if (IS_DEMO) {
        for (const key of DEMO_ENABLED_FLAGS) {
            flags[key] = true;
        }

        localStorage.removeItem(LOCAL_STORAGE_FLAGS_KEY);
    }

    return createLocalStorageLaunchDarklyClient(flags, {
        localStorageKey: LOCAL_STORAGE_FLAGS_KEY
    });
}

async function initializeDevtoolsClient(apiBaseUrl: string) {
    const { initializeApiLaunchDarklyClient } = await import("@packages/devtools/client/feature-flags/initializeApiLaunchDarklyClient.ts");

    return initializeApiLaunchDarklyClient(apiBaseUrl);
}

async function initializeLaunchDarklyClient(clientId?: string) {
    if (!clientId) {
        throw new Error("[web-app] A LaunchDarkly client id is required to initialize the LaunchDarkly client. Did you set a PUBLIC_LAUNCH_DARKLY_CLIENT_ID environment variable?");
    }

    const client = initialize(
        clientId,
        {
            kind: "user",
            anonymous: true
        },
        {
            streaming: true
        }
    );

    try {
        await client.waitForInitialization(5);

        console.info("[web-app] LaunchDarly client is initialized with the following flags:", client.allFlags());
    } catch (error: unknown) {
        console.error("[web-app] LaunchDarkly client failed to initialize:", error);
    }

    return client;
}

export async function initializeLaunchDarkly(appEnv: AppEnvironmentValue, apiBaseUrl: string, useRemoteFlagsOverride: boolean, launchDarklyClientId?: string) {
    if (!useRemoteFlagsOverride) {
        if (appEnv === "msw") {
            return initializeLocalStorageClient();
        }

        if (appEnv === "local") {
            return initializeDevtoolsClient(apiBaseUrl);
        }
    }

    return initializeLaunchDarklyClient(launchDarklyClientId);
}
